import React, { Fragment, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useMediaQuery } from 'react-responsive';
import pluralize from 'pluralize';
import pMap from 'p-map-browser';

import { getAlbumCustomPrintName } from '../../../../../../../../../../util/custom-prints';
import AccountCard from '../../../../../../../../../../services/cards/components/AccountCard';
import useAccountCardConfigure from '../../../../../../../../../../services/cards/views/AccountCardConfigurator/useAccountCardConfigure';
import {
  accountCardConfigurationValidate,
  getBackgroundAccountCardElement,
  getDefaultCardSideIds,
} from '../../../../../../../../../../services/cards/views/AccountCardConfigurator/helpers';
import Spinner from '../../../../../../../../../../components/global/Spinner';
import Button from '../../../../../../../../../../components/global/Button';
import {
  getCardPrintProductConfiguration,
  getCardPrintProductSize,
  getCardTargetAccountCardConfiguration,
  getTextElementPresetData,
} from './helpers';
import DesignEditorPreviewContent from '../../../../../../../../../../components/global/DesignEditor/components/DesignEditorPreviewPane/DesignEditorPreviewContent';
import DesignEditorPreviewPane from '../../../../../../../../../../components/global/DesignEditor/components/DesignEditorPreviewPane';
import DesignEditor from '../../../../../../../../../../components/global/DesignEditor';
import DesignEditorHeader, {
  DesignEditorHeaderCenterSlot,
  DesignEditorHeaderLeftSlot,
  DesignEditorHeaderRightSlot,
} from '../../../../../../../../../../components/global/DesignEditor/components/DesignEditorHeader';
import Angle from '../../../../../../../../../../components/global/Angle';
import ShoppingBasketIcon from '../../../../../../../../../../components/icons/ShoppingBasketIcon';
import AccountCardEmptyTextFormModal, {
  ACCOUNT_CARD_EMPTY_TEXT_FORM_MODAL,
} from './components/AccountCardEmptyTextFormModal';
import { shouldAlertEmptyText } from './components/AccountCardEmptyTextAlertModal/helpers';
import useOpenModal from '../../../../../../../../../../hooks/useOpenModal';
import useCloseModal from '../../../../../../../../../../hooks/useCloseModal';
import {
  getAccountCardElementsConfiguration,
  getCardTemplateSizing,
  isCardElementEditable,
  isCardElementTeamPhoto,
} from '../../../../../../../../../../services/cards/components/AccountCard/helpers';
import {
  CARD_ELEMENT_TYPE,
  CARD_SIDE,
  ORIENTATION,
  PHOTO_TYPE,
} from '../../../../../../../../../../services/cards/constants';
import { breakpointDown } from '../../../../../../../../../../services/responsive/responsive';
import {
  isMultiIdentityPackageMembership,
  isPictureDayMembership,
} from '../../../../../../../../../temp/views/memberships/AlbumMembershipGroup/helpers';
import useTargetAccountCardInputFetch from '../../../../../../../../../../services/cards/views/AccountCardConfigurator/useTargetAccountCardInputFetch';
import { TEXT_PRESETS } from '../../../../../../../../../../components/global/CardDesigner/components/CardDesignerCardElements/CardDesignerCardElementText/helpers';
import useIdentityJerseyColors from '../../../../../hooks/useIdentityJerseyColors';
import { getAutoFitPositionPhotoCropbox } from '../../../../../../../../../../services/cards/components/CardElementPhoto/helpers';
import { getMaxThumb } from '../../../../../../../../../../util/photo';
import { getCardPhotoElementsWithConfigurationFromAccountCard } from '../../../../../../../../../../services/prints/components/PrintProductGroupConfigurator/helpers';
import { HIERARCHY_GRADES_CARD_LABELS } from '../../../../../../../../../../constants/hierarchy-grades';

const propTypes = {
  albumCustomPrint: PropTypes.shape({
    id: PropTypes.string.isRequired,
    custom_print: PropTypes.shape({
      id: PropTypes.string.isRequired,
      card: PropTypes.shape({
        id: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
  albumMembershipGroup: PropTypes.shape({
    id: PropTypes.string.isRequired,
    is_album: PropTypes.bool,
  }),
  defaultAccountCard: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  onClose: PropTypes.func,
  onDismiss: PropTypes.func,
  className: PropTypes.string,
  altClassName: PropTypes.string,
  onFinished: PropTypes.func.isRequired,
  deleteAccountCardOnDismiss: PropTypes.bool,
  buttonText: PropTypes.node,
  identity: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  rosterIdentityLink: PropTypes.shape({
    id: PropTypes.string.isRequired,
    ref_id: PropTypes.string,
  }),
  isAllPhotos: PropTypes.bool,
  forceEntitlement: PropTypes.bool,
  photo: PropTypes.shape({
    id: PropTypes.string,
  }),
  teamPhotos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      photo: PropTypes.shape({
        id: PropTypes.string,
      }),
    }),
  ),
  title: PropTypes.string,
  inlineButton: PropTypes.bool,
  defaultName: PropTypes.string,
  onPhotoElementReferenceLoaded: PropTypes.func,
};

const PRESET_FILL_STATUS = {
  LOADING: 'LOADING',
  FILLING: 'FILLING',
  FILLED: 'FILLED',
};

const PHOTO_ELEMENT_LOAD_STATUS = {
  LOADING: 'LOADING',
  LOADED: 'LOADED',
};

const AccountCardAlbumCustomPrintConfigurator = ({
  className,
  altClassName,
  onClose,
  onDismiss,
  albumCustomPrint,
  albumMembershipGroup,
  defaultAccountCard,
  onFinished,
  deleteAccountCardOnDismiss,
  buttonText = (
    <>
      <ShoppingBasketIcon
        color="#0c84fe"
        className="mr-1 flex-none"
        size={16}
      />
      Add to cart
    </>
  ),
  identity,
  rosterIdentityLink,
  isAllPhotos = false,
  forceEntitlement = false,
  watermarkSettings,
  photo,
  teamPhotos,
  title,
  inlineButton,
  defaultName = 'Custom Print',
  onPhotoElementReferenceLoaded,
}) => {
  const classes = classNames({
    [className]: !!className,
    [altClassName]: !!altClassName,
    'modal-contained-wrap': true,
  });
  const [saving, setSaving] = useState(false);
  const [photoElementLoadStatus, setPhotoElementLoadStatus] = useState(
    PHOTO_ELEMENT_LOAD_STATUS.LOADING,
  );
  const [presetFillStatus, setPresetFillStatus] = useState(
    PRESET_FILL_STATUS.LOADING,
  );
  const [emptyTextFormSeen, setEmptyTextFormSeen] = useState(false);
  const [accountCardElementReferences, setAccountCardElementReferences] =
    useState({});
  const name = getAlbumCustomPrintName(albumCustomPrint);
  const isMobile = useMediaQuery(breakpointDown('sm'));
  const allowMultiIdentityPhoto =
    isMultiIdentityPackageMembership(albumMembershipGroup) ?? false;
  const { card } = albumCustomPrint?.custom_print ?? {};
  const printProductConfig = useMemo(
    () => getCardPrintProductConfiguration(card),
    [card],
  );
  const printProductSize = useMemo(() => getCardPrintProductSize(card), [card]);
  const linkImageElements = ['ORB_8_Trader_Cards_L'].includes(
    printProductSize?.sku,
  );

  const openModal = useOpenModal();
  const closeModal = useCloseModal();
  const {
    accountCard,
    onCardChange,
    onElementConfigurationChange,
    onAccountCardDelete,
    cardElementLoading,
  } = useAccountCardConfigure({
    accountCardId: defaultAccountCard?.id,
    presetCardElementConfigurationInputs: [],
    linkTextElements: true,
    linkImageElements,
  });
  const { teamName, levelName } = useIdentityJerseyColors({
    identityId: identity?.id,
    albumFolderId: albumMembershipGroup?.is_album
      ? albumMembershipGroup?.album?.root_album_folder?.id
      : albumMembershipGroup?.id,
  });
  const targetAccountCardConfig = useMemo(
    () => getCardTargetAccountCardConfiguration(card),
    [card],
  );
  const {
    loading: loadingTargetAccountCardProduct,
    getTargetAccountCardInput,
  } = useTargetAccountCardInputFetch({
    printProductId: printProductConfig?.print_product_id,
  });

  useEffect(() => {
    // Create the AccountCard if we're starting from scratch.
    if (!defaultAccountCard && !loadingTargetAccountCardProduct) {
      onCardChange(
        {
          card_id: card.id,
          album_custom_print_id: albumCustomPrint?.id,
          print_product_configuration: getCardPrintProductConfiguration(card),
          ...getDefaultCardSideIds(card),
        },
        true,
        getTargetAccountCardInput(targetAccountCardConfig),
      );
    }
  }, [loadingTargetAccountCardProduct]);

  const editablePhotoElements = useMemo(() => {
    const elements = getAccountCardElementsConfiguration(
      accountCard,
      cardElementLoading,
      linkImageElements,
    );

    return (elements ?? []).filter(
      element =>
        element.cardElement.type === CARD_ELEMENT_TYPE.PHOTO &&
        element.cardElementConfiguration.photo_type !== PHOTO_TYPE.LOGO &&
        element.side === CARD_SIDE.FRONT &&
        isCardElementEditable(element),
    );
  }, [accountCard, cardElementLoading]);

  const portraitEditablePhotoElements = useMemo(() => {
    const { width: cardWidth, height: cardHeight } = getCardTemplateSizing(
      card?.card_side_groups?.records?.[0]?.card_sides?.records?.[0]
        .card_template,
    );

    return editablePhotoElements.filter(
      element =>
        !isCardElementTeamPhoto(element) &&
        cardWidth * element.cardElement?.frame?.width <
          cardHeight * element.cardElement?.frame?.height,
    );
  }, [card, editablePhotoElements]);

  const landscapeEditablePhotoElements = useMemo(() => {
    const { width: cardWidth, height: cardHeight } = getCardTemplateSizing(
      card?.card_side_groups?.records?.[0]?.card_sides?.records?.[0]
        .card_template,
    );

    return editablePhotoElements.filter(
      element =>
        !isCardElementTeamPhoto(element) &&
        cardWidth * element.cardElement?.frame?.width >=
          cardHeight * element.cardElement?.frame?.height,
    );
  }, [card, editablePhotoElements]);

  const teamEditablePhotoElements = useMemo(
    () =>
      editablePhotoElements.filter(element => isCardElementTeamPhoto(element)),
    [editablePhotoElements],
  );

  useEffect(() => {
    const onFillEditableElements = async ({
      editableTextElements,
      editableBarCodeElements,
      editableLogoElements,
    }) => {
      setPresetFillStatus(PRESET_FILL_STATUS.FILLING);

      const { width: cardWidth, height: cardHeight } = getCardTemplateSizing(
        card?.card_side_groups?.records?.[0]?.card_sides?.records?.[0]
          .card_template,
      );

      // Fill team photo slots from teamPhotos in index order (photo 0 ->
      // slot 0, photo 1 -> slot 1, ...) until either runs out. We can do this
      // regardless of whether a single `photo` is provided because we now
      // identify team slots semantically via help_placeholder.
      if (teamEditablePhotoElements.length > 0 && teamPhotos?.length > 0) {
        const teamFillCount = Math.min(
          teamEditablePhotoElements.length,
          teamPhotos.length,
        );

        for (let i = 0; i < teamFillCount; i += 1) {
          const teamSlot = teamEditablePhotoElements[i];
          const teamPhoto = teamPhotos[i];
          const { cardElement, cardElementConfiguration } = teamSlot ?? {};

          if (cardElementConfiguration?.photo?.id) {
            continue;
          }

          const teamThumb = getMaxThumb(teamPhoto?.photo?.thumbnail_urls);

          await onElementConfigurationChange(teamSlot, {
            photo_id: teamPhoto?.photo?.id,
            is_editable: false,
            cropbox: getAutoFitPositionPhotoCropbox({
              cardElement,
              cardWidth,
              cardHeight,
              thumb: teamThumb,
              faceboxes: (teamPhoto?.photo?.faces?.records ?? []).map(
                ({ facebox }) => facebox,
              ),
            }),
          });
        }
      }

      if (
        editablePhotoElements?.length === 1 &&
        !isCardElementTeamPhoto(editablePhotoElements[0]) &&
        photo
      ) {
        const { cardElement, cardElementConfiguration } =
          editablePhotoElements[0] ?? {};

        // We don't want to replace the cropbox if there is an existing configuration.
        if (!cardElementConfiguration?.photo?.id) {
          const thumb = getMaxThumb(photo?.thumbnail_urls);
          const { width: photoWidth, height: photoHeight } = thumb ?? {};
          const photoIsLandscape = photoWidth >= photoHeight;
          const parsedFrame = cardElement?.frame
            ? {
                width:
                  Math.round(cardElement?.frame?.width * cardWidth * 1e2) / 1e2,
                height:
                  Math.round(cardElement?.frame?.height * cardHeight * 1e2) /
                  1e2,
              }
            : undefined;

          const frameOrientation =
            parsedFrame?.height > parsedFrame?.width
              ? ORIENTATION.PORTRAIT
              : ORIENTATION.LANDSCAPE;

          const orientationsMatch =
            (frameOrientation === ORIENTATION.LANDSCAPE && photoIsLandscape) ||
            (frameOrientation === ORIENTATION.PORTRAIT && !photoIsLandscape);

          await onElementConfigurationChange(editablePhotoElements[0], {
            photo_id: photo.id,
            is_editable: true,
            cropbox: getAutoFitPositionPhotoCropbox({
              cardElement,
              cardWidth,
              cardHeight,
              thumb,
              faceboxes: (photo?.faces?.records ?? []).map(
                ({ facebox }) => facebox,
              ),
              autoFitToFace: !orientationsMatch && photo?.faces?.total === 1,
            }),
          });
        }
      } else if (
        (portraitEditablePhotoElements.length === 1 ||
          landscapeEditablePhotoElements.length === 1) &&
        photo
      ) {
        const thumb = getMaxThumb(photo?.thumbnail_urls);
        const { width: photoWidth, height: photoHeight } = thumb ?? {};
        const photoIsLandscape = photoWidth >= photoHeight;

        if (photoIsLandscape && landscapeEditablePhotoElements.length === 1) {
          const { cardElement, cardElementConfiguration } =
            landscapeEditablePhotoElements[0] ?? {};

          if (!cardElementConfiguration?.photo?.id) {
            await onElementConfigurationChange(
              landscapeEditablePhotoElements[0],
              {
                photo_id: photo.id,
                is_editable: true,
                cropbox: getAutoFitPositionPhotoCropbox({
                  cardElement,
                  cardWidth,
                  cardHeight,
                  thumb,
                  faceboxes: (photo?.faces?.records ?? []).map(
                    ({ facebox }) => facebox,
                  ),
                }),
              },
            );
          }
        } else if (
          teamEditablePhotoElements.length === 0 &&
          teamPhotos?.length > 0 &&
          landscapeEditablePhotoElements.length === 1
        ) {
          // Backward compatibility: if the card has no dedicated team slots,
          // fall back to filling a lone landscape slot from the first
          // teamPhoto when the primary `photo` is portrait.
          const [teamPhoto] = teamPhotos;
          const teamThumb = getMaxThumb(teamPhoto?.photo?.thumbnail_urls);

          const { cardElement, cardElementConfiguration } =
            landscapeEditablePhotoElements[0] ?? {};

          if (!cardElementConfiguration?.photo?.id) {
            await onElementConfigurationChange(
              landscapeEditablePhotoElements[0],
              {
                photo_id: teamPhoto?.photo?.id,
                is_editable: false,
                cropbox: getAutoFitPositionPhotoCropbox({
                  cardElement,
                  cardWidth,
                  cardHeight,
                  thumb: teamThumb,
                  faceboxes: (teamPhoto?.photo?.faces?.records ?? []).map(
                    ({ facebox }) => facebox,
                  ),
                }),
              },
            );
          }
        }

        if (!photoIsLandscape && portraitEditablePhotoElements.length === 1) {
          const { cardElement, cardElementConfiguration } =
            portraitEditablePhotoElements[0] ?? {};

          if (!cardElementConfiguration?.photo?.id) {
            await onElementConfigurationChange(
              portraitEditablePhotoElements[0],
              {
                photo_id: photo.id,
                is_editable: true,
                cropbox: getAutoFitPositionPhotoCropbox({
                  cardElement,
                  cardWidth,
                  cardHeight,
                  thumb,
                  faceboxes: (photo?.faces?.records ?? []).map(
                    ({ facebox }) => facebox,
                  ),
                }),
              },
            );
          }
        }
      }

      await pMap(editableBarCodeElements, async element => {
        if (rosterIdentityLink?.ref_id) {
          await onElementConfigurationChange(element, {
            code: rosterIdentityLink.ref_id,
          });
        } else {
          await onElementConfigurationChange(element, {
            code: '',
            opacity: 0,
            is_editable: false,
          });
        }
      });

      await pMap(editableLogoElements, async element => {
        const assetImage =
          // Parsed from an albumMembershipGroup. PurchasingOptions instance
          albumCustomPrint?.logo_image ??
          albumCustomPrint?.album?.logo_image ??
          albumCustomPrint?.album?.root_album_folder?.logo_image ??
          albumCustomPrint?.album?.creator?.logo_image;

        if (assetImage?.id) {
          await onElementConfigurationChange(element, {
            existing_asset_image_id: assetImage.id,
            photo_type: PHOTO_TYPE.LOGO,
            is_editable: false,
          });
        } else {
          await onElementConfigurationChange(element, {
            opacity: 0,
            is_editable: false,
          });
        }
      });

      await pMap(editableTextElements, async element => {
        const { cardElementConfiguration } = element ?? {};
        const presetKey = Object.keys(TEXT_PRESETS).find(
          k => TEXT_PRESETS[k] === cardElementConfiguration?.help_placeholder,
        );
        // We only want to automatically update values where a preset is established.
        // Therefore, we ensure a matching key exists on our array.
        if (!cardElementConfiguration?.text && presetKey) {
          const text = getTextElementPresetData({
            presetLabel: TEXT_PRESETS[presetKey],
            identity,
            albumMembershipGroup,
            teamName,
            levelName,
            gradeName: rosterIdentityLink?.grade
              ? HIERARCHY_GRADES_CARD_LABELS[rosterIdentityLink?.grade]
              : '',
            departmentName: rosterIdentityLink?.department ?? '',
          });
          if (text) {
            await onElementConfigurationChange(element, { text });
          } else {
            // Pass dumb text here. The opacity will hide it, but the dumb content
            // will make the system assume that the entity is "filled" for empty text
            // slot purposes.
            await onElementConfigurationChange(element, {
              text: 'NULL',
              opacity: 0,
              is_editable: false,
            });
          }
        }
      });

      setPresetFillStatus(PRESET_FILL_STATUS.FILLED);
    };

    if (
      presetFillStatus === PRESET_FILL_STATUS.LOADING &&
      accountCard &&
      !cardElementLoading
    ) {
      const elementsForEditing = getAccountCardElementsConfiguration(
        accountCard,
        cardElementLoading,
      );
      const editableTextElements = elementsForEditing.filter(
        element =>
          element.cardElement.type === CARD_ELEMENT_TYPE.TEXT &&
          element.side === CARD_SIDE.FRONT &&
          isCardElementEditable(element),
      );
      const editableBarCodeElements = elementsForEditing.filter(
        element =>
          element.cardElement.type === CARD_ELEMENT_TYPE.BARCODE &&
          element.side === CARD_SIDE.FRONT &&
          isCardElementEditable(element),
      );
      const editableLogoElements = elementsForEditing.filter(
        element =>
          element.cardElement.type === CARD_ELEMENT_TYPE.PHOTO &&
          element.cardElementConfiguration.photo_type === PHOTO_TYPE.LOGO &&
          element.side === CARD_SIDE.FRONT &&
          isCardElementEditable(element),
      );

      if (
        editableTextElements.length > 0 ||
        editableBarCodeElements?.length > 0 ||
        editableLogoElements?.length > 0 ||
        ((editablePhotoElements?.length === 1 ||
          portraitEditablePhotoElements?.length === 1 ||
          landscapeEditablePhotoElements?.length === 1) &&
          photo) ||
        (teamEditablePhotoElements?.length > 0 && teamPhotos?.length > 0)
      ) {
        onFillEditableElements({
          editableTextElements,
          editableBarCodeElements,
          editableLogoElements,
        });
      } else {
        setPresetFillStatus(PRESET_FILL_STATUS.FILLED);
      }
    }
  }, [accountCard, cardElementLoading]);

  useEffect(() => {
    if (
      accountCard &&
      !emptyTextFormSeen &&
      presetFillStatus === PRESET_FILL_STATUS.FILLED &&
      shouldAlertEmptyText({ accountCard })
    ) {
      openModal(ACCOUNT_CARD_EMPTY_TEXT_FORM_MODAL, {
        accountCard,
        onElementConfigurationChange,
        onComplete: () => {},
        canGoBack: false,
      });
      setEmptyTextFormSeen(true);
    }
  }, [accountCard, presetFillStatus, emptyTextFormSeen]);

  const onAddAccountCardElementReference = ({ cardElementId, ref }) => {
    setAccountCardElementReferences({
      ...accountCardElementReferences,
      [cardElementId]: ref,
    });
  };

  const hasPhotoElementsMissingConfiguration = useMemo(() => {
    return !!editablePhotoElements.find(
      element =>
        !element?.cardElementConfiguration?.photo?.id &&
        !element?.cardElementConfiguration?.asset_image?.id,
    );
  }, [accountCard, cardElementLoading]);

  const backgroundElement = getBackgroundAccountCardElement({
    accountCard,
  });

  const photoElements = getCardPhotoElementsWithConfigurationFromAccountCard({
    accountCard,
  }).filter(
    elem =>
      elem?.cardElement?.id !== backgroundElement?.cardElement?.id &&
      (!elem.cardElementConfiguration ||
        elem.cardElementConfiguration?.is_editable),
  );

  const [mainPhotoElement] = photoElements ?? [];

  const photoElementReference =
    accountCardElementReferences?.[mainPhotoElement?.cardElement?.id];

  const onFinishAccountCard = async () => {
    if (saving) {
      return;
    }

    if (shouldAlertEmptyText({ accountCard })) {
      openModal(ACCOUNT_CARD_EMPTY_TEXT_FORM_MODAL, {
        accountCard,
        onElementConfigurationChange,
        onComplete: async () => {
          setSaving(true);
          await onFinished({ accountCard });
          setSaving(false);
          closeModal(ACCOUNT_CARD_EMPTY_TEXT_FORM_MODAL);
        },
        title: "Heads up! You're print is missing the info below:",
        buttonText: 'Save and finish',
      });
    }
    try {
      setSaving(true);
      await onFinished({ accountCard });
    } catch {
      //   Do nothing
    } finally {
      setSaving(false);
    }
  };

  const accountCardMainPhotoActionClasses = classNames({
    'account-card-album-custom-print-configurator-actions': true,
    'account-card-album-custom-print-configurator-actions-landscape':
      accountCard?.account_card_sides?.[0]?.card_side?.card_template
        ?.orientation === ORIENTATION.LANDSCAPE,
  });

  const accountCardClasses = classNames({
    'account-card-album-custom-print-configurator': true,
    'account-card-album-custom-print-configurator-landscape':
      accountCard?.account_card_sides?.[0]?.card_side?.card_template
        ?.orientation === ORIENTATION.LANDSCAPE,
  });

  useEffect(() => {
    if (
      photoElementReference &&
      photoElements?.length === 1 &&
      photoElementLoadStatus === PHOTO_ELEMENT_LOAD_STATUS.LOADING &&
      onPhotoElementReferenceLoaded
    ) {
      onPhotoElementReferenceLoaded(photoElementReference);
      setPhotoElementLoadStatus(PHOTO_ELEMENT_LOAD_STATUS.LOADED);
    }
  }, [
    photoElementLoadStatus,
    photoElementReference,
    onPhotoElementReferenceLoaded,
  ]);

  return (
    <div className={classes}>
      <DesignEditor>
        <DesignEditorHeader>
          <DesignEditorHeaderLeftSlot>
            {onClose && (
              <Button
                btnStyle="link"
                className="text-bold"
                onClick={() => {
                  if (deleteAccountCardOnDismiss) {
                    onAccountCardDelete();
                  }
                  onClose();
                  onDismiss?.();
                }}
              >
                <Angle direction="left" />
              </Button>
            )}
          </DesignEditorHeaderLeftSlot>
          <DesignEditorHeaderCenterSlot>
            <h3 className="design-editor-header-title">
              {title ?? `Configure your ${name ? name : defaultName}`}
            </h3>
          </DesignEditorHeaderCenterSlot>

          <DesignEditorHeaderRightSlot>
            {!inlineButton && (
              <Button
                btnSize="sm"
                className="px-6 btn-round"
                btnOutline
                onClick={onFinishAccountCard}
                disabled={
                  !accountCardConfigurationValidate(accountCard) || saving
                }
              >
                {saving ? 'Saving...' : buttonText}
              </Button>
            )}
          </DesignEditorHeaderRightSlot>
        </DesignEditorHeader>
        <DesignEditorPreviewPane>
          <DesignEditorPreviewContent className="flex-column">
            {accountCard && presetFillStatus === PRESET_FILL_STATUS.FILLED ? (
              <Fragment>
                <h4 className="d-md-none text-center">
                  {title ?? `Configure your ${name}`}
                </h4>
                <div className={accountCardMainPhotoActionClasses}>
                  {photoElementReference && photoElements?.length === 1 && (
                    <Fragment>
                      {photoElementReference?.onPhotoChange && (
                        <Button
                          btnStyle="link"
                          btnSize="sm"
                          className="text-extra-bold"
                          onClick={() => {
                            photoElementReference.onPhotoChange();
                          }}
                        >
                          Change Photo
                        </Button>
                      )}
                      {photoElementReference?.onAdjustAndCrop && (
                        <Button
                          btnStyle="link"
                          btnSize="sm"
                          className="text-extra-bold"
                          onClick={() => {
                            photoElementReference.onAdjustAndCrop();
                          }}
                        >
                          Adjust &amp; Crop
                        </Button>
                      )}
                    </Fragment>
                  )}
                </div>
                <AccountCard
                  className={accountCardClasses}
                  onElementConfigurationChange={onElementConfigurationChange}
                  accountCard={accountCard}
                  cardElementLoading={cardElementLoading}
                  disablePhotoWatermarkOverlay={forceEntitlement}
                  photosContext={{
                    identity_id: allowMultiIdentityPhoto
                      ? undefined
                      : identity?.id,
                    album_id:
                      albumMembershipGroup &&
                      (albumMembershipGroup.is_album
                        ? albumMembershipGroup.id
                        : undefined),
                    album_folder_id:
                      albumMembershipGroup &&
                      (!albumMembershipGroup.is_album
                        ? albumMembershipGroup.id
                        : undefined),
                    root_album_folder_id:
                      albumMembershipGroup && albumMembershipGroup.is_album
                        ? albumMembershipGroup.album.root_album_folder?.id
                        : undefined,
                    is_album_membership_group:
                      !!albumMembershipGroup?.identities ||
                      albumMembershipGroup?.is_pro_access,
                    is_all_photos: isAllPhotos,
                    force_entitlement:
                      forceEntitlement || albumMembershipGroup?.is_pro_access,
                    uploads_disabled:
                      !!isPictureDayMembership(albumMembershipGroup) &&
                      !albumMembershipGroup?.is_pro_access,
                    is_pro_access: albumMembershipGroup?.is_pro_access,
                  }}
                  watermarkSettings={watermarkSettings}
                  linkImageElements={linkImageElements}
                  onAddAccountCardElementReference={
                    onAddAccountCardElementReference
                  }
                  teamPhotos={teamPhotos}
                />

                {hasPhotoElementsMissingConfiguration && (
                  <p className="text-italic text-center text-sm mt-2 mb-0">
                    {isMobile ? 'Tap' : 'Click'} on camera to choose your{' '}
                    {pluralize('photo', editablePhotoElements?.length ?? 0)}
                  </p>
                )}
                {!hasPhotoElementsMissingConfiguration &&
                  photoElements?.length > 1 && (
                    <p className="text-italic text-center text-sm mt-2 mb-0">
                      {isMobile ? 'Tap' : 'Click'} on a photo above to change
                      the photo or alter the crop.
                    </p>
                  )}
                {inlineButton && (
                  <Button
                    btnBlock
                    className="mt-2 btn-round inline-account-card-configurator-button"
                    btnOutline
                    onClick={() => {
                      if (shouldAlertEmptyText({ accountCard })) {
                        openModal(ACCOUNT_CARD_EMPTY_TEXT_FORM_MODAL, {
                          accountCard,
                          onElementConfigurationChange,
                          onComplete: () => {
                            onFinished({ accountCard });
                          },
                        });
                      } else {
                        onFinished({ accountCard });
                      }
                    }}
                    disabled={!accountCardConfigurationValidate(accountCard)}
                  >
                    {buttonText}
                  </Button>
                )}
              </Fragment>
            ) : (
              <div className="account-card-album-custom-print-configurator d-flex">
                <Spinner className="m-auto" />
              </div>
            )}
          </DesignEditorPreviewContent>
        </DesignEditorPreviewPane>
      </DesignEditor>
      <AccountCardEmptyTextFormModal />
    </div>
  );
};

AccountCardAlbumCustomPrintConfigurator.propTypes = propTypes;

export default AccountCardAlbumCustomPrintConfigurator;
